// Gruvetto score (0 to 1000): the rider's racing level, which replaces the W/kg letters. ADR 0009, docs/BALANCE.md.
// - Power score: best efforts of the last 90 days (or the estimated FTP if higher), in W/kg, corrected for mass: at equal W/kg the
//   engine favours heavier riders. It is also the floor of the score.
// - Race results: multiplayer Elo between verified humans of a server-simulated race (bots carry no information:
//   they are tuned to the rider).
// - Anchoring: every week the level and scale of the scores are pulled towards the power score (a zero-sum Elo has neither).
// The parameters come from the balance bench (tools/balance, "recommended" config): change them there first.
import type { ModeId } from './modes.js';

export const RATING = {
  /** 200 points per W/kg, 0 at 1 W/kg: 20 points = 0.1 W/kg. */
  ptsPerWkg: 200,
  max: 1000,
  /** Mass correction: W/kg-equivalent per ln(mass / refMass), measured on the engine. */
  refMass: 74,
  massTerm: 0.9,
  /** Best efforts counted in the power profile (days). */
  profileDays: 90,
  /**
   * FTP-equivalent of the best efforts counted in the profile. The best of them wins: a weighted mean would punish a
   * rider who never sprinted. One-minute power is left out, it flatters sprinters.
   */
  profileEfforts: [
    { dur: 1200, factor: 0.95 },
    { dur: 300, factor: 0.82 },
  ],
  /** Elo: divisor (points for 10:1 odds), K provisional then established, largest change in one race. */
  divisor: 60,
  kProvisional: 60,
  kEstablished: 24,
  provisionalRaces: 10,
  cap: 40,
  /** Weekly anchoring: share of the gap closed, riders counted (raced in the last N days, at least N races). */
  anchorPull: 0.25,
  anchorEveryDays: 7,
  anchorActiveDays: 30,
  anchorMinRaces: 10,
  anchorMinRiders: 20,
} as const;

/** Modes whose results move the score. Free ride, ghost and workout do not measure a racing level. */
export const RATED_MODES: readonly ModeId[] = ['race', 'tt', 'elim'];
export const isRatedMode = (mode: string): boolean => (RATED_MODES as readonly string[]).includes(mode);

/** A rider's rating state. `power` is the power score, `score` the race-adjusted score, `races` the rated races. */
export interface Rating {
  score: number;
  power: number;
  races: number;
}

const clampScore = (v: number) => Math.max(0, Math.min(RATING.max, v));
const round1 = (v: number) => Math.round(v * 10) / 10;

/**
 * FTP-equivalent W/kg of the power profile: the best FTP-equivalent of the 20 and 5 minute efforts, never below the
 * FTP the server estimated (ftp.ts), which may be a test result or the weight-based start.
 */
export function profileWkg(bests: Record<number, number>, ftp: number, mass: number): number {
  const m = mass > 0 ? mass : RATING.refMass;
  let best = Math.max(0, ftp);
  for (const { dur, factor } of RATING.profileEfforts) best = Math.max(best, (bests[dur] || 0) * factor);
  return best / m;
}

/** Power score from a profile W/kg and the rider's mass. */
export function powerScore(wkg: number, mass: number): number {
  const m = mass > 0 ? mass : RATING.refMass;
  return round1(clampScore(RATING.ptsPerWkg * (wkg + RATING.massTerm * Math.log(m / RATING.refMass) - 1)));
}

/** W/kg that gives `score` to a rider of `mass`: how strong a bot of that mass must be to match the score. */
export function wkgForScore(score: number, mass: number): number {
  const m = mass > 0 ? mass : RATING.refMass;
  return Math.max(0.5, score / RATING.ptsPerWkg + 1 - RATING.massTerm * Math.log(m / RATING.refMass));
}

/** Score shown and used: the power score until the first rated race, then the race-adjusted score, floored by the power. */
export function currentScore(r: Rating | null | undefined, power: number): number {
  if (!r || r.races <= 0) return round1(power);
  return round1(Math.max(r.score, power));
}

export const isProvisional = (r: Rating | null | undefined): boolean => !r || r.races < RATING.provisionalRaces;

/**
 * Multiplayer Elo on a finishing order (index 0 = winner): every pair counts, averaged over the field so that one race
 * moves a rider by at most K. Returns the new ratings, in the same order. Fewer than two riders: unchanged.
 */
export function eloUpdate(order: readonly Rating[]): Rating[] {
  const n = order.length;
  if (n < 2) return order.map(r => ({ ...r }));
  const before = order.map(r => currentScore(r, r.power));
  return order.map((r, i) => {
    let S = 0,
      E = 0;
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      S += i < j ? 1 : 0;
      E += 1 / (1 + 10 ** ((before[j] - before[i]) / RATING.divisor));
    }
    const K = r.races < RATING.provisionalRaces ? RATING.kProvisional : RATING.kEstablished;
    const delta = Math.max(-RATING.cap, Math.min(RATING.cap, (K * (S - E)) / (n - 1)));
    return { score: round1(Math.max(r.power, clampScore(before[i] + delta))), power: r.power, races: r.races + 1 };
  });
}

/** Anchoring transform: scores move to `mean + offset + (score - mean) × factor`. */
export interface Anchor {
  mean: number;
  offset: number;
  factor: number;
}

/** Computes the weekly anchoring from the active riders, or null when there are too few or no usable spread. */
export function anchorFor(active: readonly Rating[]): Anchor | null {
  if (active.length < RATING.anchorMinRiders) return null;
  const ms = active.reduce((a, r) => a + r.score, 0) / active.length;
  const mp = active.reduce((a, r) => a + r.power, 0) / active.length;
  let num = 0,
    den = 0;
  for (const r of active) {
    num += (r.power - mp) * (r.score - ms);
    den += (r.power - mp) ** 2;
  }
  const slope = den ? num / den : NaN;
  if (!(slope > 0)) return null;
  return { mean: ms, offset: RATING.anchorPull * (mp - ms), factor: 1 + RATING.anchorPull * (1 / slope - 1) };
}

export function applyAnchor(r: Rating, a: Anchor): Rating {
  if (r.races <= 0) return { ...r };
  return { ...r, score: round1(Math.max(r.power, clampScore(a.mean + a.offset + (r.score - a.mean) * a.factor))) };
}
